import { ArrowRight, Check, FileText, Search, Compass, ListChecks, ShieldCheck } from "lucide-react";
import { TopBar } from "@/components/TopBar";
import { Navbar } from "@/components/Navbar";
import { Footer } from "@/components/Footer";
import { Reveal } from "@/components/Reveal";
import { CreditReviewForm } from "@/components/CreditReviewForm";
import { usePageMeta } from "@/hooks/usePageMeta";
import { BUSINESS } from "@/lib/constants";

const ScrollCta = ({ label, testId }) => (
  <a
    href="#credit-review"
    data-testid={testId}
    className="group inline-flex h-12 items-center justify-center gap-2 rounded-lg bg-gradient-to-b from-[#E7C766] to-[#AE8924] px-8 text-sm font-semibold text-black shadow-md shadow-[#C9A227]/20 transition-colors duration-200 hover:from-[#F2D47E] hover:to-[#C49B2E]"
  >
    {label}
    <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
  </a>
);

const steps = [
  {
    number: "01",
    icon: FileText,
    title: "Submit Your Information",
    description: "Complete the short form so our team can learn more about your credit situation.",
    testId: "cr-step-1",
  },
  {
    number: "02",
    icon: Search,
    title: "We Review Your Situation",
    description:
      "Our team reviews the information you provide and identifies areas that may require closer attention.",
    testId: "cr-step-2",
  },
  {
    number: "03",
    icon: Compass,
    title: "Understand Your Options",
    description:
      "We explain potential next steps and help you understand what may be appropriate based on your circumstances.",
    testId: "cr-step-3",
  },
  {
    number: "04",
    icon: ListChecks,
    title: "Decide How You Want to Proceed",
    description:
      "After the review, you can decide whether you would like to move forward with the available service options.",
    testId: "cr-step-4",
  },
];

const reviewableItems = [
  "Collections",
  "Charge-offs",
  "Late payments",
  "Repossessions",
  "Hard inquiries",
  "Personal-information inaccuracies",
  "Duplicate accounts",
  "Other potentially inaccurate or questionable reporting",
];

export default function CreditRepairPage() {
  usePageMeta(
    "Credit Report Review | J11 Consulting",
    "J11 Consulting helps consumers understand their credit reports and learn what options may be available to address inaccurate, incomplete, outdated, or unverifiable information."
  );

  return (
    <div data-testid="credit-repair-page">
      <TopBar />
      <Navbar />
      <main>
        {/* Hero */}
        <section data-testid="cr-hero" className="bg-[#08080A] py-14 lg:py-20">
          <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
            <Reveal>
              <p className="text-[11px] font-bold uppercase tracking-[0.24em] text-[#D8B54A]" data-testid="cr-hero-badge">
                Credit Report Review
              </p>
              <h1
                data-testid="cr-hero-headline"
                className="mt-3 max-w-3xl font-heading text-3xl font-bold tracking-tight text-white sm:text-4xl lg:text-5xl"
              >
                Understand What&rsquo;s Affecting Your Credit
              </h1>
              <p className="mt-4 max-w-2xl text-base font-medium leading-relaxed text-[#E4DFD3] sm:text-lg" data-testid="cr-hero-subheadline">
                Get a professional review of your credit situation and learn what options may be
                available to address inaccurate, incomplete, outdated, or unverifiable information
                on your credit reports.
              </p>
              <p className="mt-3 max-w-2xl text-sm leading-relaxed text-[#8D877B] sm:text-base">
                Every credit situation is different. {BUSINESS.shortName} helps consumers better
                understand their credit reports, identify potential issues, and determine
                appropriate next steps based on their individual circumstances.
              </p>
              <div className="mt-8">
                <ScrollCta label="Request a Credit Review" testId="cr-hero-cta" />
              </div>
            </Reveal>
          </div>
        </section>

        {/* Educational introduction */}
        <section data-testid="cr-intro" className="bg-[#0E0E12] py-14 lg:py-20">
          <div className="mx-auto max-w-3xl px-4 sm:px-6 lg:px-8">
            <Reveal>
              <p className="text-[11px] font-bold uppercase tracking-[0.24em] text-[#D8B54A]">
                Credit Education
              </p>
              <h2 className="mt-3 font-heading text-2xl font-bold tracking-tight text-[#F2EEE3] sm:text-3xl lg:text-4xl">
                Start With Understanding, Not Guesswork
              </h2>
              <p className="mt-5 text-base leading-relaxed text-[#B8B2A6] sm:text-lg">
                Many consumers are unsure why their credit looks the way it does, or which items on
                their reports deserve closer attention. {BUSINESS.shortName} provides educational
                credit report reviews designed to help you understand what is being reported, why it
                matters, and what options may be available.
              </p>
              <p className="mt-4 text-base leading-relaxed text-[#B8B2A6] sm:text-lg">
                If our team identifies information that appears inaccurate, incomplete, outdated, or
                unverifiable, we explain what may be eligible to be challenged where appropriate —
                and what realistic next steps could look like for your situation.
              </p>
            </Reveal>
          </div>
        </section>

        {/* How It Works */}
        <section data-testid="cr-how-it-works" className="bg-[#141419] py-14 lg:py-20">
          <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
            <Reveal className="text-center">
              <p className="text-[11px] font-bold uppercase tracking-[0.24em] text-[#D8B54A]">How It Works</p>
              <h2 className="mx-auto mt-3 max-w-2xl font-heading text-2xl font-bold tracking-tight text-[#F2EEE3] sm:text-3xl lg:text-4xl">
                A Simple, Transparent Process
              </h2>
            </Reveal>
            <div className="mt-12 grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
              {steps.map((step, i) => (
                <Reveal key={step.number} delay={i * 0.08}>
                  <div
                    data-testid={step.testId}
                    className="group h-full rounded-2xl border border-white/10 bg-[#17171C] p-7 transition-all duration-300 hover:-translate-y-1 hover:shadow-lg hover:shadow-black/40"
                  >
                    <div className="flex items-center justify-between">
                      <span className="flex h-12 w-12 items-center justify-center rounded-xl bg-[#141419] transition-colors duration-300 group-hover:bg-[#C9A227]/10">
                        <step.icon className="h-5 w-5 text-[#D8B54A]" strokeWidth={1.8} />
                      </span>
                      <span className="flex h-12 w-12 items-center justify-center rounded-xl bg-[#08080A] font-heading text-base font-bold text-white shadow-md shadow-black/40">
                        {step.number}
                      </span>
                    </div>
                    <h3 className="mt-6 font-heading text-lg font-semibold tracking-tight text-[#F2EEE3]">
                      Step {i + 1} — {step.title}
                    </h3>
                    <p className="mt-3 text-sm leading-relaxed text-[#B8B2A6]">{step.description}</p>
                  </div>
                </Reveal>
              ))}
            </div>
          </div>
        </section>

        {/* What Can Be Reviewed */}
        <section data-testid="cr-reviewable" className="bg-[#0E0E12] py-14 lg:py-20">
          <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
            <Reveal>
              <p className="text-[11px] font-bold uppercase tracking-[0.24em] text-[#D8B54A]">Credit Report Review</p>
              <h2 className="mt-3 max-w-2xl font-heading text-2xl font-bold tracking-tight text-[#F2EEE3] sm:text-3xl lg:text-4xl">
                What Can Be Reviewed?
              </h2>
              <p className="mt-5 max-w-2xl text-base leading-relaxed text-[#B8B2A6]">
                Credit reports can sometimes contain information that is inaccurate, incomplete,
                outdated, duplicated, or otherwise requires clarification.
              </p>
              <p className="mt-3 max-w-2xl text-sm leading-relaxed text-[#8D877B]">
                Depending on the individual situation, our team may review items such as:
              </p>
            </Reveal>
            <div className="mt-10 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
              {reviewableItems.map((item, i) => (
                <Reveal key={item} delay={i * 0.05}>
                  <div
                    data-testid={`cr-reviewable-${item.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                    className="group flex h-full items-center gap-3 rounded-xl border border-white/10 bg-[#17171C] px-5 py-4 transition-all duration-300 hover:-translate-y-1 hover:border-[#C9A227]/30 hover:shadow-lg hover:shadow-black/40"
                  >
                    <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-[#C9A227]/10">
                      <Check className="h-4 w-4 text-[#D8B54A]" strokeWidth={2.5} />
                    </span>
                    <p className="text-sm font-medium text-[#F2EEE3]">{item}</p>
                  </div>
                </Reveal>
              ))}
            </div>
            <Reveal delay={0.15}>
              <p
                className="mt-8 rounded-xl border border-[#C9A227]/30 bg-[#C9A227]/10 px-6 py-4 text-[13px] leading-relaxed text-[#E4DFD3]"
                data-testid="cr-reviewable-note"
              >
                Not every negative item is inaccurate or eligible to be changed. Results depend on
                the facts of each individual case.
              </p>
            </Reveal>
          </div>
        </section>

        {/* Transparency */}
        <section data-testid="cr-transparency" className="bg-[#08080A] py-14 lg:py-20">
          <Reveal className="mx-auto max-w-3xl px-4 sm:px-6 lg:px-8">
            <div className="rounded-2xl border border-white/10 bg-[#121216] p-7 sm:p-10">
              <div className="flex items-center gap-3">
                <span className="flex h-10 w-10 items-center justify-center rounded-xl bg-[#C9A227]/10">
                  <ShieldCheck className="h-5 w-5 text-[#D8B54A]" strokeWidth={1.8} />
                </span>
                <h2 className="font-heading text-xl font-bold tracking-tight text-[#F2EEE3] sm:text-2xl" data-testid="cr-transparency-heading">
                  Clear Information. No Unrealistic Promises.
                </h2>
              </div>
              <div className="mt-5 space-y-4 text-sm leading-relaxed text-[#B8B2A6] sm:text-base">
                <p>
                  {BUSINESS.shortName} does not promise a specific credit-score increase, a
                  particular deletion, financing approval, or a guaranteed outcome.
                </p>
                <p>
                  Our goal is to help you better understand your credit situation, identify
                  potential reporting issues, and determine appropriate next steps.
                </p>
              </div>
            </div>
          </Reveal>
        </section>

        {/* Credit Review Request Form */}
        <section id="credit-review" data-testid="cr-form-section" className="scroll-mt-24 bg-[#0E0E12] py-14 lg:py-20">
          <Reveal className="mx-auto max-w-3xl px-4 text-center sm:px-6">
            <p className="text-[11px] font-bold uppercase tracking-[0.24em] text-[#D8B54A]">Get Started</p>
            <h2 className="mx-auto mt-3 font-heading text-2xl font-bold tracking-tight text-white sm:text-3xl lg:text-4xl" data-testid="cr-form-section-heading">
              Tell Us About Your Credit Situation
            </h2>
            <p className="mx-auto mt-5 max-w-xl text-base leading-relaxed text-[#C6C0B4]">
              Complete the short form below. A member of the {BUSINESS.shortName} team will review
              your information and contact you regarding the next steps.
            </p>
          </Reveal>
          <Reveal delay={0.1} className="mx-auto mt-10 max-w-xl px-4 sm:px-6">
            <CreditReviewForm />
          </Reveal>
        </section>
      </main>
      <Footer />
    </div>
  );
}
